import Vuex, { Store } from 'vuex';
import DeviceType from '@/enums/DeviceType';
import { configureSentryScopeTags } from '../helpers/sentryManager';

export enum LightStatus {
  Disconnected,
  Idle,
  Connecting,
  WrongPassword,
  APNotFound,
  ConnectionFailed,
  IPObtained,
}

export type WaitingForCredentialsStatus = {
  type: 'waitingForCredentials';
  ssidNotFound: boolean;
  passwordIsWrong: boolean;
  ssidList: Array<string>;
};

export type EncryptingCredentialsStatus = {
  type: 'encryptingCredentials';
};

export type ConnectingToTheLightStatus = {
  type: 'connectingToTheLight';
};

export type SendingCredentialsStatus = {
  type: 'sendingCredentials';
  hintTimeoutReached: boolean;
};

export type WaitingDeviceToConnectToTheWifiStatus = {
  type: 'waitingDeviceToConnectToTheWiFi';
  hintTimeoutReached: boolean;
};

export type CreatingDeviceInCloudStatus = {
  type: 'creatingDeviceInCloud';
  hintTimeoutReached: boolean;
};

export type FinishingPairingStatus = {
  type: 'finishingPairing';
  hintTimeoutReached: boolean;
};

export type EncryptingCredentialsFailStatus = {
  type: 'encryptingCredentialsFail';
};

export type CreateLightInCloudFailStatus = {
  type: 'createLightInCloudFail';
};

export type LightConnectionToWifiFailStatus = {
  type: 'lightConnectionToWifiFail';
};

export type PairingStatus =
  | WaitingForCredentialsStatus
  | EncryptingCredentialsStatus
  | ConnectingToTheLightStatus
  | SendingCredentialsStatus
  | WaitingDeviceToConnectToTheWifiStatus
  | CreatingDeviceInCloudStatus
  | FinishingPairingStatus
  | EncryptingCredentialsFailStatus
  | CreateLightInCloudFailStatus
  | LightConnectionToWifiFailStatus;

export type LightCredentials = {
  enc_ssid: string;
  enc_pwd: string;
  pwd: string;
  offsets: Array<number>;
  iv: string;
  hid: number;
  env: string;
  reg: string;
};
type State = {
  registrationData: {
    accessToken: string;
    homeId: number;
    roomId: number;
    ssid: string;
    password: string;
    env: string;
    region: string;
    thirdParty: string | undefined;
  } | undefined,
  lightStatus: LightStatus;
  lightMacAddress: string | undefined;
  lightIp: string | undefined;
  pairingInProgress: boolean;
  lightCreationTimerToken: any | undefined;
  credentialsForTheLight: LightCredentials | undefined;
  pairingStatus: PairingStatus;
  visibleSSIDs: Array<string>;
  currentSSID: string;
  lightSSID: string;
  pairingAttempt: number;
  deviceType: DeviceType | undefined;
  isShortcut: boolean | undefined;
};
const initialState: State = {
  registrationData: undefined,
  lightStatus: LightStatus.Disconnected,
  lightMacAddress: undefined,
  lightIp: undefined,
  pairingInProgress: false,
  lightCreationTimerToken: undefined,
  credentialsForTheLight: undefined,
  visibleSSIDs: [],
  currentSSID: '',
  lightSSID: '',
  pairingStatus: {
    type: 'waitingForCredentials',
    ssidNotFound: false,
    passwordIsWrong: false,
    ssidList: [],
  },
  pairingAttempt: 1,
  deviceType: undefined,
  isShortcut: undefined,
};

export default () =>
  new Vuex.Store({
    state: initialState,
    getters: {
      status(state: State) {
        return {
          pairingStatus: state.pairingStatus,
          lightStatus: state.lightStatus,
          currentSSID: state.currentSSID,
        };
      },
      credentialsForTheLight(state: State) {
        return state.credentialsForTheLight;
      },
      accessToken(state: State) {
        return state.registrationData?.accessToken;
      },
    },
    mutations: {
      injectWiZData(
        state,
        dt: {
          accessToken: string;
          homeId: number;
          roomId: number;
          ssid: string;
          password: string;
          env: string;
          region: string;
          deviceType: DeviceType | undefined;
          isShortcut: boolean | undefined;
          thirdParty: string | undefined;
        },
      ) {
        state.registrationData = {
          accessToken: dt.accessToken,
          homeId: dt.homeId,
          roomId: dt.roomId,
          ssid: dt.ssid,
          password: dt.password,
          env: dt.env,
          region: dt.region,
          thirdParty: dt.thirdParty
        };
        state.currentSSID = dt.ssid;

        state.deviceType = dt.deviceType || DeviceType.Light;
        state.isShortcut = dt.isShortcut || false;
        configureSentryScopeTags(state.registrationData.homeId, state.lightMacAddress);
      },
      updateLightStatus(
        state,
        lightState: {
          status: number;
          mac: string;
          visibleSSIDs: Array<string>;
          scan: Array<{ ssid: string; rssi: number }>;
          ip: string;
        },
      ) {
        const processSSIDs = (
          ssids: Array<{ ssid: string; rssi: number }>,
        ): Array<string> => {
          return ssids
            .sort((a, b) => (a.rssi < b.rssi ? 1 : 0))
            .map(el => el.ssid);
        };
        state.lightMacAddress = lightState.mac;
        configureSentryScopeTags(state.registrationData?.homeId, state.lightMacAddress);
        switch (lightState.status) {
          case 0:
            state.lightStatus = LightStatus.Idle;
            return;
          case 1:
            state.lightStatus = LightStatus.Connecting;
            return;
          case 2:
            state.lightStatus = LightStatus.WrongPassword;
            return;
          case 3:
            state.lightStatus = LightStatus.APNotFound;
            state.visibleSSIDs = processSSIDs(lightState.scan);
            return;
          case 4:
            state.lightStatus = LightStatus.ConnectionFailed;
            state.visibleSSIDs = processSSIDs(lightState.scan);
            return;
          case 5:
            state.lightStatus = LightStatus.IPObtained;
            state.lightIp = lightState.ip;
            return;
        }
      },
      pairingStarted(state) {
        state.pairingInProgress = true;
      },
      pairingFinished(state) {
        state.pairingInProgress = false;
      },
      lightCredentialsUpdated(
        state,
        params: {
          wifi_ssid: string;
          wifi_password: string;
          iv: string;
          offsets: Array<number>;
        },
      ) {
        if (state.registrationData != undefined) {
          state.credentialsForTheLight = {
            enc_ssid: params.wifi_ssid,
            enc_pwd: params.wifi_password,
            pwd: state.registrationData.password.slice(
              state.registrationData.password.length / 2,
              state.registrationData.password.length,
            ),
            offsets: params.offsets,
            iv: params.iv,
            hid: state.registrationData.homeId,
            env: state.registrationData.env,
            reg: state.registrationData.region,
          };
        }
      },
      cleanEncryptedCredentialsWrongPassword(state) {
        state.credentialsForTheLight = undefined;
        state.pairingAttempt++;
      },
      cleanEncryptedCredentialsWrongSsid(state) {
        state.credentialsForTheLight = undefined;
        state.lightSSID = '';
        state.pairingAttempt++;
      },
      cleanStateToEnterPassword(state) {
        state.pairingStatus = {
          type: 'waitingForCredentials',
          ssidNotFound: false,
          passwordIsWrong: true,
          ssidList: [],
        };
        state.lightStatus = LightStatus.Disconnected;
      },
      clearStateToEnterSSID(state) {
        state.pairingStatus = {
          type: 'waitingForCredentials',
          ssidNotFound: true,
          passwordIsWrong: false,
          ssidList: state.visibleSSIDs,
        };
        state.lightStatus = LightStatus.Disconnected;
      },
      updateSSID(state, ssid: string) {
        if (state.registrationData != undefined) {
          state.registrationData.ssid = ssid;
        }
      },
      updatePassword(state, password: string) {
        if (state.registrationData != undefined) {
          state.registrationData.password = password;
        }
      },
      updatePairingStatus(state, newStatus: PairingStatus) {
        state.pairingStatus = newStatus;
      },
      currentSSIDChanged(state, ssid: string) {
        state.currentSSID = ssid;
      },
      lightSSIDDiscovered(state, ssid: string) {
        state.lightSSID = ssid;
      },
    },
    strict: process.env.NODE_ENV !== 'production',
  });



// WEBPACK FOOTER //
// ./src/store/index.ts